import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from './config';

interface PricingOption {
  plan: 'monthly' | 'quarterly' | 'annual';
  name: string;
  amount: number;
  displayAmount: string;
  description: string;
  installments: number;
  totalCost: number;
  emoji: string;
  savings: string | null;
  intervalDays: number;
}

const Billing: React.FC = () => {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'quarterly' | 'annual' | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTier, setCurrentTier] = useState<number>(1);
  const [hasPaidForCourse, setHasPaidForCourse] = useState<boolean>(false);
  const loginButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isAuthenticated && window.innerWidth < 768) {
      setTimeout(() => {
        loginButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        loginButtonRef.current?.focus();
      }, 300);
    }
  }, [isAuthenticated]);

  const pricingOptions: PricingOption[] = [
    {
      plan: 'monthly',
      name: 'Monthly',
      amount: 19.99,
      displayAmount: '$19.99',
      description: 'Billed monthly. Your subscription tier increases as you keep learning.',
      installments: 1,
      totalCost: 19.99,
      emoji: '📅',
      savings: null,
      intervalDays: 30
    },
    {
      plan: 'quarterly',
      name: 'Quarterly',
      amount: 49.99,
      displayAmount: '$49.99',
      description: 'Billed every 3 months. Great balance of value and flexibility.',
      installments: 1,
      totalCost: 49.99,
      emoji: '📊',
      savings: null,
      intervalDays: 90
    },
    {
      plan: 'annual',
      name: 'Annual',
      amount: 99.99,
      displayAmount: '$99.99',
      description: 'Billed yearly. Best value for committed learners.',
      installments: 1,
      totalCost: 99.99,
      emoji: '⭐',
      savings: null,
      intervalDays: 365
    }
  ];

  useEffect(() => {
    if (isAuthenticated && user?.sub && courseId) {
      // Fetch user's payment status for this course
      const fetchUserStatus = async () => {
        try {
          const response = await fetch(`${config.apiBaseUrl}/api/user/course-payment-status/${courseId}?sub=${user.sub}`);
          if (response.ok) {
            const statusData = await response.json();
            setHasPaidForCourse(statusData.hasPaid || false);
            setCurrentTier(statusData.subscriptionTier || 1);
          }
        } catch (err) {
          console.error('Error fetching payment status:', err);
        }
      };
      fetchUserStatus();
    }
  }, [isAuthenticated, user?.sub, courseId]);

  const handleCheckout = async (plan: 'monthly' | 'quarterly' | 'annual') => {
    if (!isAuthenticated) {
      loginWithRedirect?.({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      });
      return;
    }

    if (!courseId) {
      alert('Please select a course before proceeding');
      navigate('/practice');
      return;
    }

    setSelectedPlan(plan);
    setLoading(true);

    try {
      const requestBody = {
        frequency: plan, // Reuse the frequency parameter for payment plan
        courseId: parseInt(courseId),
        userSub: user?.sub,
        userEmail: user?.email
      };
      console.log('Sending checkout request:', requestBody);

      const response = await fetch(`${config.apiBaseUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        }
      } else {
        const error = await response.json();
        console.error('Checkout error response:', error);
        alert(`Error: ${error.error || JSON.stringify(error)}`);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      alert('Error processing payment. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userSub: user?.sub
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
      }
    } catch (err) {
      console.error('Error creating portal session:', err);
      alert('Error accessing billing portal. Please try again.');
    }
  };

  return (
    <div style={{
      maxWidth: 1200,
      margin: '0 auto',
      padding: '40px 20px'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 60 }}>
        <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 12, color: '#1a1a1a' }}>
          Course Subscription
        </h1>
        <p style={{ fontSize: 18, color: '#666', marginBottom: 8 }}>
          Choose monthly, quarterly, or annual billing.
        </p>
        {isAuthenticated && hasPaidForCourse && (
          <p style={{ fontSize: 14, color: '#10b981', fontWeight: 600, marginTop: 12 }}>
            ✓ You have full access to this course
          </p>
        )}
        {!courseId && (
          <p style={{ fontSize: 14, color: '#dc2626', fontWeight: 600, marginTop: 12 }}>
            📚 Please select a course to view pricing
          </p>
        )}
      </div>

      {/* Not authenticated message */}
      {!isAuthenticated && (
        <div style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: 8,
          padding: 20,
          marginBottom: 40,
          textAlign: 'center'
        }}>
          <p style={{ color: '#1565c0', marginBottom: 12 }}>
            Please log in to upgrade your subscription
          </p>
          <button
            ref={loginButtonRef}
            onClick={() =>
              loginWithRedirect?.({
                appState: {
                  returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                },
              })
            }
            style={{
              backgroundColor: '#2196f3',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '10px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Log In to Continue
          </button>
        </div>
      )}

      {/* Pricing Cards */}
      {courseId && !hasPaidForCourse && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 24,
          marginBottom: 40
        }}>
          {pricingOptions.map((option) => {
            const isSelected = selectedPlan === option.plan;

            return (
              <div
                key={option.plan}
                style={{
                  border: isSelected ? '2px solid #2563eb' : option.plan === 'annual' ? '2px solid #10b981' : '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 28,
                  backgroundColor: isSelected ? '#f0f9ff' : option.plan === 'annual' ? '#f0fdf4' : '#fff',
                  boxShadow: isSelected ? '0 10px 25px rgba(37, 99, 235, 0.1)' : option.plan === 'annual' ? '0 10px 25px rgba(16, 185, 129, 0.1)' : '0 1px 3px rgba(0,0,0,0.1)',
                  position: 'relative',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                  transform: isSelected || option.plan === 'annual' ? 'scale(1.02)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && option.plan !== 'annual') {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  } else if (!isSelected && option.plan === 'annual') {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.1)';
                  }
                }}
              >
                {option.savings && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    right: 20,
                    backgroundColor: '#10b981',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600
                  }}>
                    {option.savings}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 32, marginRight: 8 }}>{option.emoji}</span>
                  <h3 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#1a1a1a' }}>
                    {option.name}
                  </h3>
                </div>

                <p style={{ fontSize: 14, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
                  {option.description}
                </p>

                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f3f4f6', borderRadius: 8 }}>
                  <span style={{ fontSize: 36, fontWeight: 700, color: '#1a1a1a' }}>
                    {option.displayAmount}
                  </span>
                  {option.installments > 1 && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                      Total: ${option.totalCost} over {option.installments} payments
                    </div>
                  )}
                </div>

                {option.installments > 1 && (
                  <div style={{ marginBottom: 20, padding: '12px', backgroundColor: '#eff6ff', borderRadius: 8 }}>
                    <p style={{ fontSize: 12, color: '#1e40af', fontWeight: 600, margin: 0, lineHeight: 1.5 }}>
                      📅 Payment Schedule:<br/>
                      • 1st payment: ${option.amount} today<br/>
                      {option.installments === 2 && `• 2nd payment: $${option.amount} in 30 days`}
                      {option.installments === 3 && `• 2nd payment: $${option.amount} in 30 days`}<br/>
                      {option.installments === 3 && `• 3rd payment: $${option.amount} in 60 days`}
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      if (window.innerWidth < 768) {
                        loginButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        loginButtonRef.current?.focus();
                      } else {
                        loginWithRedirect?.({
                          appState: {
                            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                          },
                        });
                      }
                      return;
                    }
                    handleCheckout(option.plan);
                  }}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    borderRadius: 6,
                    border: 'none',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginBottom: 24,
                    transition: 'background-color 0.2s ease',
                    backgroundColor: isSelected ? '#1d4ed8' : option.plan === 'annual' ? '#10b981' : '#2563eb',
                    color: '#fff',
                    opacity: loading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && isAuthenticated && !loading) {
                      e.currentTarget.style.backgroundColor = option.plan === 'annual' ? '#059669' : '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && isAuthenticated && !loading) {
                      e.currentTarget.style.backgroundColor = option.plan === 'annual' ? '#10b981' : '#2563eb';
                    }
                  }}
                >
                  {loading && selectedPlan === option.plan ? 'Processing...' : 'Subscribe'}
                </button>

                {/* Benefits List */}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ padding: '8px 0', fontSize: 14, color: '#666', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ marginRight: 8, color: '#10b981' }}>✓</span>
                    Access to premium content while subscribed
                  </li>
                  <li style={{ padding: '8px 0', fontSize: 14, color: '#666', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ marginRight: 8, color: '#10b981' }}>✓</span>
                    All practice questions unlocked
                  </li>
                  <li style={{ padding: '8px 0', fontSize: 14, color: '#666', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ marginRight: 8, color: '#10b981' }}>✓</span>
                    Billing frequency can be changed anytime
                  </li>
                  <li style={{ padding: '8px 0', fontSize: 14, color: '#666' }}>
                    <span style={{ marginRight: 8, color: '#10b981' }}>✓</span>
                    {option.plan === 'monthly' ? 'Billed monthly' : option.plan === 'quarterly' ? 'Billed every 3 months' : 'Billed annually'}
                  </li>
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Already Paid Message */}
      {courseId && hasPaidForCourse && (
        <div style={{
          backgroundColor: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: 8,
          padding: 32,
          marginBottom: 40,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#065f46', marginBottom: 12 }}>
            You Have Full Access
          </h2>
          <p style={{ color: '#047857', marginBottom: 20, fontSize: 16 }}>
            You already have access to this course.
          </p>
          <button
            onClick={() => navigate(`/practice?courseId=${courseId}`)}
            style={{
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#059669';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#10b981';
            }}
          >
            Go to Practice Questions
          </button>
        </div>
      )}

      {/* No Course Selected Message */}
      {!courseId && isAuthenticated && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: 8,
          padding: 20,
          marginBottom: 40,
          textAlign: 'center'
        }}>
          <p style={{ color: '#92400e', marginBottom: 12 }}>
            Please select a course from the Questions page to view pricing options
          </p>
          <button
            onClick={() => navigate('/practice')}
            style={{
              backgroundColor: '#fbbf24',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: 4,
              padding: '10px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Go to Questions
          </button>
        </div>
      )}

      {/* Manage Subscription Button */}
      {isAuthenticated && hasPaidForCourse && (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button
            onClick={handleManageSubscription}
            style={{
              backgroundColor: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4b5563';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6b7280';
            }}
          >
            Manage Payment Methods
          </button>
        </div>
      )}

      {/* Information Section */}
      <div style={{ marginTop: 80, maxWidth: 800, margin: '80px auto 0' }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32, textAlign: 'center', color: '#1a1a1a' }}>
          How Tier Progression Works
        </h2>

        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ padding: 20, backgroundColor: '#f0f9ff', borderRadius: 8, borderLeft: '4px solid #2563eb' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              📅 Monthly Billing
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Pay $19.99 each month. Your subscription tier increases by 1 with each payment.
            </p>
          </div>

          <div style={{ padding: 20, backgroundColor: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #fbbf24' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              📊 Quarterly Billing
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Pay $49.99 every 3 months. Your tier increases by 1 with each quarterly payment.
            </p>
          </div>

          <div style={{ padding: 20, backgroundColor: '#ecfdf5', borderRadius: 8, borderLeft: '4px solid #10b981' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              ⭐ Annual Billing (Best Value)
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Pay $99.99 once per year. Your subscription tier is set to MAXIMUM immediately.
            </p>
          </div>

          <div style={{ padding: 20, backgroundColor: '#ede9fe', borderRadius: 8, borderLeft: '4px solid #a855f7' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              🔓 Tier System
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Your subscription tier corresponds to content accessibility levels in categories. Higher tiers unlock more advanced and exclusive content.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div style={{ marginTop: 60, maxWidth: 800, margin: '60px auto 0' }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32, textAlign: 'center', color: '#1a1a1a' }}>
          Frequently Asked Questions
        </h2>

        <div style={{ display: 'grid', gap: 20 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              Can I change my billing frequency?
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Yes! You can manage your subscription in your account settings and switch between monthly, quarterly, or annual billing.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              What happens when my subscription ends?
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Your subscription tier will return to the base level (tier 1). You can resubscribe anytime to regain access to premium content.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              Is there a free trial?
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              Yes! You can access free course content and practice questions without subscribing. Subscribe to unlock premium content.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              What payment methods do you accept?
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              We accept all major credit cards and debit cards through Stripe's secure payment gateway.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
              How do refunds work?
            </h3>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              We offer a 7-day money-back guarantee. If you're not satisfied, contact our support team for a full refund.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
